import React, { useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import nanoid from 'nanoid'

import { Fab, Tooltip, CircularProgress, IconButton } from '@mui/material'
import { AttachFile, Delete, DeleteForever } from '@mui/icons-material'
import { createAttachment, removeAttachment, removeAttachmentFromAnswerObjectOnly } from 'utils/attachmentOperations'
import Colors from 'utils/Colors'
import { toast } from 'react-toastify'

import _ from 'lodash'
import styles from './styles'

const ERROR_MESSAGE = `Houve um erro ao criar o(s) anexo(s). Tente novamente.`

const AttachmentListItem = ({ attachmentData, onRemoveAttachment, disabled }) => {
  const classes = styles()
  return (
    <div className={classes.listItem}>
      <a href={attachmentData?.document_url} target="_blank">
        {attachmentData?.file_name}
      </a>
      <Tooltip title="Remover anexo">
        <span>
          <IconButton
            onClick={() => onRemoveAttachment(attachmentData)}
            className={classes.deleteButton}
            disabled={disabled}
          >
            <Delete fontSize="small" />
            <DeleteForever fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  )
}

const AddAttachments = ({
    targetFilename,
    error,
    title,
    attachments,
    updateAttachments,
    allowSingleFileOnly = false,
    disabled = false,
    answerId = null,
    deleteFromAnswerOnly = false,
  }) => {
  const classes = styles()
  const buttonId = nanoid()
  const inputRef = useRef(null)
  const token = useSelector((state) => state.auth.access_token)

  const [loading, setLoading] = useState(false)

  const handleFileUpload = async (event) => {
    const { files } = event.target
    setLoading(true)
    const filesToSave = Array.from(files).filter(
      (file) =>
        !attachments.some((internFile) => internFile.name === file.name),
    )

    Promise.all(filesToSave.map((file) => createAttachment({
      token: token, attachment: file, targetFilename: targetFilename,
    })))
      .then((values) => {
        const processedValues = values.map((value) => value?.data)
        if (
          processedValues.length > 0 &&
          processedValues.every((value) => value?.uuid)
        ) {
          updateAttachments([...attachments, ...processedValues])
        } else {
          toast.error(ERROR_MESSAGE)
        }
      })
      .catch(() => toast.error(ERROR_MESSAGE))
      .finally(() => {
        setLoading(false)
        inputRef.current.value = null
      })
  }

  const callRemoveFileApi = (attachment, newInternAttachment) => {
    let apiPromise = null
    if (deleteFromAnswerOnly) {
      apiPromise =  removeAttachmentFromAnswerObjectOnly(attachment?.uuid, answerId, token)
    }
    else {
      apiPromise =  removeAttachment(attachment?.uuid, answerId, token)
    }

    apiPromise.then(() => updateAttachments(newInternAttachment))
    .catch(() => toast.error("Ocorreu um erro ao apagar o arquivo"))
    .finally(() => {
      setLoading(false)
      inputRef.current.value = null
    })
  }

  const handleRemoveAttachment = (attachment) => {
    if (deleteFromAnswerOnly && _.isEmpty(answerId)) {
        console.warn('Cannnot delete attachment from answer only because there is no answer_uuid')
        inputRef.current = null
        updateAttachments([])
        return
    }

    setLoading(true)
    const newInternAttachment = attachments.filter(
      (internAttachment) => attachment?.uuid !== internAttachment?.uuid,
    )
    
    callRemoveFileApi(attachment, newInternAttachment)
  }

  return (
    <div
      className={classes.root}
      style={{ borderColor: error ? Colors.red : Colors.lightGray }}
    >
      <h4 className={classes.attachmentsTitle}>{title}</h4>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <ul className={classes.attachmentList}>
          {attachments?.length > 0 &&
            attachments?.map((attachment) => (
              <li key={attachment?.uuid}>
                <AttachmentListItem
                  attachmentData={attachment}
                  onRemoveAttachment={handleRemoveAttachment}
                  disabled={disabled}
                />
              </li>
            ))}
        </ul>
      )}
      {(!allowSingleFileOnly || _.isEmpty(attachments)) && (
        <label htmlFor={`button-file-${buttonId}`} className={classes.fabContainer}>
          <input
            id={`button-file-${buttonId}`}
            name="file"
            type="file"
            onChange={handleFileUpload}
            multiple={!allowSingleFileOnly}
            disabled={disabled}
            style={{ display: 'none' }}
            ref={inputRef}
          />
          <Tooltip title={allowSingleFileOnly ? 'Adicionar arquivo' : 'Adicionar arquivos'}>
            <Fab
              color="primary"
              aria-label="Add"
              component="div"
              disabled={loading || disabled}
              size="small"
              className={classes.fab}
            >
              <AttachFile />
            </Fab>
          </Tooltip>
        </label>
      )}
    </div>
  )
}

export default AddAttachments
